import { useState, useRef, useEffect } from 'react';
import { C } from '../utils/constants';

const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds === Infinity) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const AudioPlayer = ({ src, title, onListened }) => {
  const audioRef = useRef(null);
  // Fire onListened at most once per mount when playback crosses 90% of the
  // track. NOTE: trusts playback position, so dragging the scrubber near the
  // end also counts as listened — accepted limitation for now.
  const firedRef = useRef(false);
  const onListenedRef = useRef(onListened);
  onListenedRef.current = onListened;
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (!isDragging) setCurrentTime(audio.currentTime);
      if (!firedRef.current && audio.duration > 0 && audio.currentTime / audio.duration >= 0.9) {
        firedRef.current = true;
        if (typeof onListenedRef.current === 'function') onListenedRef.current();
      }
    };
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isDragging]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    const newTime = pct * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      background: C.veryLightBrown,
      border: `1.5px solid ${C.border}`,
      borderRadius: 12,
      padding: '16px 20px',
    }}>
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Title */}
      {title && (
        <p style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '0.95rem',
          letterSpacing: '0.04em',
          marginBottom: 14,
        }}>
          {title}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Play/pause button */}
        <button
          onClick={togglePlay}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.brown}, ${C.amber})`,
            border: 'none',
            color: '#fff',
            fontSize: '1.1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 3px 12px rgba(107,62,10,0.3)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 5px 18px rgba(107,62,10,0.4)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 3px 12px rgba(107,62,10,0.3)';
          }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>

        {/* Progress section */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Progress bar */}
          <div
            style={{
              height: 6,
              background: C.border,
              borderRadius: 3,
              cursor: 'pointer',
              marginBottom: 6,
              position: 'relative',
            }}
            onClick={handleProgressClick}
          >
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${C.amber}, ${C.gold})`,
              borderRadius: 3,
              transition: isDragging ? 'none' : 'width 0.1s linear',
            }} />
            {/* Thumb indicator */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: `${progress}%`,
              transform: 'translate(-50%, -50%)',
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: C.amber,
              boxShadow: `0 0 6px rgba(232,131,10,0.5)`,
              transition: isDragging ? 'none' : 'left 0.1s linear',
            }} />
          </div>

          {/* Time display */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: C.terracotta,
            fontWeight: 500,
          }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
